import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ReplaceServerResultDto } from '@his/shared';
import { post } from '@/lib/api';
import { copyText, dateTime } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { OwnerConfirmDialog } from './OwnerConfirm';

function ReplaceServerResult({ result, onClose }: { result: ReplaceServerResultDto; onClose: () => void }) {
  const toast = useToast();
  const text = [`Activation token: ${result.token}`, `Expires: ${dateTime(result.expiresAt)}`, '', ...result.instructions.map((line, index) => `${index + 1}. ${line}`)].join('\n');
  return (
    <Dialog
      open
      onClose={onClose}
      title="New server ready to activate"
      description="The token is shown only once. Send it with the steps below to whoever installs the new server."
      footer={
        <>
          <Button variant="outline" onClick={() => void copyText(text).then((ok) => (ok ? toast.success('Copied') : toast.error('Could not copy')))}>
            Copy token and steps
          </Button>
          <Button onClick={onClose}>Done</Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <div className="rounded-md border border-success/30 bg-success/5 p-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Activation token — expires {dateTime(result.expiresAt)}</p>
          <p className="mt-1 select-all break-all font-mono text-sm">{result.token}</p>
        </div>
        <ol className="list-decimal space-y-1 ps-5">
          {result.instructions.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ol>
      </div>
    </Dialog>
  );
}

export function ReplaceServerDialog({ tenantId, tenantName, onClose }: { tenantId: string; tenantName: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<ReplaceServerResultDto | null>(null);
  return (
    <>
      {result && <ReplaceServerResult result={result} onClose={onClose} />}
      {!result && (
        <OwnerConfirmDialog
          open
          onClose={onClose}
          title={`Replace the server at ${tenantName}`}
          description="For a server that died or is being swapped for new hardware."
          warning="The old server stops syncing and checking in immediately. A new activation token is issued for the replacement, together with the steps to restore the latest local backup."
          confirmLabel="Replace server"
          destructive
          successMessage="Old server retired; new activation token issued"
          action={(payload) => post<ReplaceServerResultDto>(`/owner/tenants/${tenantId}/replace-server`, payload)}
          onDone={(issued) => {
            void queryClient.invalidateQueries({ queryKey: ['tenant-deployment', tenantId] });
            setResult(issued);
          }}
        />
      )}
    </>
  );
}
